import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { CtaSection } from "@/components/CtaSection";

const PACKAGES = [
  {
    name: "Launch",
    price: "$1,799",
    was: "$2,149",
    tag: "Launch offer",
    for: "For early-stage brands and local businesses",
    includes: [
      "Basic growth audit",
      "1 ad channel (Meta or Google)",
      "4 ad creatives / month",
      "Simple landing page optimization",
      "Monthly reporting call",
    ],
    cta: "Start with Launch",
    featured: false,
  },
  {
    name: "Scale",
    price: "$2,599",
    was: null,
    tag: "Most popular",
    for: "For growing ecommerce, SaaS, or service brands",
    includes: [
      "Growth strategy session",
      "Up to 2 ad channels (e.g., Meta + Google)",
      "8 ad creatives / month",
      "Landing page & funnel improvements",
      "Email/SMS flow setup (1 core flow)",
      "Bi-weekly optimization calls",
      "Monthly performance report",
    ],
    cta: "Go Scale",
    featured: true,
  },
  {
    name: "Everything",
    price: "$4,449",
    was: null,
    tag: "Partner",
    for: "For brands ready to invest heavily in growth",
    includes: [
      "Dedicated growth support",
      "Multi-channel strategy (Meta, Google, TikTok, etc.)",
      "12+ ad creatives / month",
      "Advanced funnel & CRO work",
      "Full email/SMS system (multiple flows)",
      "Weekly strategy + optimization calls",
      "Custom reporting dashboard",
    ],
    cta: "Go Everything",
    featured: false,
  },
];

export default function Pricing() {
  return (
    <>
      <Seo
        title="Packages & Pricing — Covelli"
        siteName="Covelli"
        description="Transparent marketing packages in CAD: Launch, Scale and Everything. Custom plans for serious brands."
      />
      <section data-testid="pricing-hero" className="mx-auto max-w-7xl px-6 pb-6 pt-32 md:pt-40">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-kinetic"
        >
          Pricing
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-4xl font-extrabold tracking-tighter text-slate-900 sm:text-5xl md:text-6xl"
        >
          Packages & Pricing
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
          className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600"
        >
          Transparent starting points. Custom plans for serious brands. All
          prices in CAD.
        </motion.p>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-12">
        <div className="grid gap-6 lg:grid-cols-3">
          {PACKAGES.map((p, i) => (
            <motion.article
              key={p.name}
              data-testid={`package-card-${p.name.toLowerCase()}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className={`relative flex flex-col rounded-2xl border bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:p-10 ${
                p.featured ? "border-kinetic shadow-[0_20px_60px_rgb(255,77,0,0.12)]" : "border-slate-200/80"
              }`}
            >
              <span
                className={`absolute -top-3 left-8 rounded-full px-3.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest ${
                  p.featured ? "bg-kinetic text-white" : "bg-skybrand-light text-skybrand-hover border border-skybrand-border"
                }`}
              >
                {p.tag}
              </span>
              <h2 className="font-display text-xl font-bold text-slate-900">{p.name}</h2>
              <p className="mt-2 text-sm text-slate-500">{p.for}</p>
              <div className="mt-7 flex items-end gap-3">
                <span className="font-display text-5xl font-extrabold tracking-tight text-kinetic">
                  {p.price}
                </span>
                <span className="pb-1.5 font-mono text-xs uppercase tracking-widest text-slate-400">
                  / month
                </span>
              </div>
              {p.was && (
                <p className="mt-1.5 font-mono text-xs text-slate-400">
                  <span className="line-through">{p.was}</span> — launch pricing
                </p>
              )}
              <ul className="mt-8 flex-1 space-y-3.5">
                {p.includes.map((inc) => (
                  <li key={inc} className="flex items-start gap-3 text-sm leading-relaxed text-slate-700">
                    <Check size={15} className="mt-0.5 shrink-0 text-skybrand" />
                    {inc}
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                data-testid={`package-cta-${p.name.toLowerCase()}`}
                className="group mt-9 inline-flex items-center justify-center gap-2 rounded-full bg-kinetic px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-kinetic-hover"
              >
                {p.cta}
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-16 rounded-2xl border border-slate-200/80 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:p-10"
        >
          <h2 className="font-display text-2xl font-bold text-slate-900">Need something custom?</h2>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-slate-600">
            One-off audits, funnel builds, creative sprints, and tailored plans.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-5">
            <Link
              to="/contact"
              data-testid="custom-quote-button"
              className="group inline-flex items-center gap-2 rounded-full bg-kinetic px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-kinetic-hover"
            >
              Request a Custom Quote
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/faq"
              data-testid="pricing-faq-link"
              className="font-mono text-[11px] uppercase tracking-widest text-skybrand transition-colors duration-300 hover:text-skybrand-hover"
            >
              Have questions about pricing or fit? See our FAQ
            </Link>
          </div>
        </motion.div>
      </section>

      <CtaSection
        testid="pricing-cta"
        headline="Ready to stop guessing and start scaling?"
        subtext="No pressure. Just a clear plan and honest answers."
        buttonText="Book Your Strategy Call"
      />
    </>
  );
}
